/**
 * Loads the page data from the website API.
 *
 * @returns {Object} Containing the page and the fetchPage method.
 */
export const usePage = () => {
	const route = useRoute();
	const { locale, defaultLocale } = useI18n();

	const pages = useState('pages', () => new Map());

	/**
	 * Get current page data reactively based on route.
	 *
	 * @returns {Object} The current page data.
	 */
	const page = computed(() => {
		return pages.value.get(route.path) || {};
	});

	/**
	 * Sets the page state for the current route.
	 *
	 * @param {*} data The page data.
	 *
	 * @returns {void} Nothing.
	 */
	const setPage = (data) => {
		if (!data || typeof data.value === 'undefined') {
			return;
		}

		pages.value.set(route.path, data.value);
	};

	/**
	 * Fetches the page data from the API and sets it in the page state.
	 *
	 * @returns {void} Nothing.
	 */
	const fetchPage = async () => {
		const pageFilename = useFilename(route, locale.value);

		try {
			if (pathContainsDefaultLocale(route)) {
				// The default locale is served on the root path, so if the route path contains the default locale,
				// we show a 404 page.
				return setPage(await fetch404Page());
			}

			const key = `page-${route.path}`;
			const { data: pageData } = await useWebsiteFetch('pages', {
				query: { filename: pageFilename, language: locale.value },
				key,
			});

			// No page found, so fetch the 404 page
			if (!pageData.value || !Object.keys(pageData.value).length) {
				return setPage(await fetch404Page());
			}

			return setPage(pageData);
		} catch (_error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching page:', route.path);

			return null;
		}
	};

	/**
	 * Fetches the 404 page from the API.
	 *
	 * @returns {Promise<Object>} The page data.
	 */
	const fetch404Page = async () => {
		const fallback = {
			title: 'Page not found',
			content: 'The page you are looking for does not exist.',
		};

		const key = `${locale.value}/404`;

		try {
			const { data: result } = await useWebsiteFetch('pages', {
				query: { filename: '404', language: locale.value },
				key,
			});

			const event = useRequestEvent();
			setResponseStatus(event, 404);

			return result?.value || fallback;
		} catch (_error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching 404 page:', key);

			return fallback;
		}
	};

	/**
	 * Checks if the route path contains the default locale.
	 *
	 * @param {Object} route The route object
	 *
	 * @returns {Boolean} Whether or not the route path contains the default locale.
	 */
	const pathContainsDefaultLocale = (route) => {
		return route.path === `/${defaultLocale}` || route.path.startsWith(`/${defaultLocale}/`);
	};

	return {
		page,
		fetchPage,
	};
};
